<template>
  <div class="profile-meta" :class="{isEditing: isAnyOpen, isSettings}">
    <div class="name-row">
      <!-- the name after the homepage icon in Settings, before it elsewhere: in the markup, so the
           Tab order follows what's seen (2026-10-14; was CSS order) -->
      <slot v-if="!isSettings" name="name"><span class="name">{{ name }}</span></slot>
      <!-- editable: buttons opening the edit bands; otherwise links (the browser shows the URL on
           hover; the externalLinks plugin opens them in a new tab) -->
      <component
        :is="editable ? 'button' : 'a'"
        v-if="editable || personalUrl"
        ref="urlIconRef"
        class="icon-btn"
        :class="{dimmed: !personalUrl, isHidden: openFields.url}"
        :href="editable ? undefined : personalUrl"
        :title="urlIconTitle"
        @click="editable && openHomepage()"
      >
        <AppModalProfileHomepageMark is-flush />
      </component>
      <slot v-if="isSettings" name="name"><span class="name">{{ name }}</span></slot>
      <div class="meta-icons">
        <component
          :is="editable ? 'button' : 'a'"
          v-if="editable || hasLinkedIn"
          ref="linkedInIconRef"
          class="icon-btn"
          :class="{dimmed: !hasLinkedIn, isHidden: openFields.linkedIn}"
          :href="editable ? undefined : linkedInUrl"
          :title="linkedInTitle"
          @click="editable && openLinkedIn()"
        >
          <AppModalProfileLinkedInMark />
        </component>
      </div>
    </div>
    <div v-if="affiliationDisplay || editable" class="affiliation-row">
      <!-- editable (Settings): the text itself opens the affiliation edit -->
      <button
        v-if="editable"
        class="affiliation isButton"
        :class="{placeholder: !affiliationDisplay}"
        type="button"
        title="Update your affiliation or country"
        @click="openAffiliation">
        <!-- each part on one line, cut with "…" when too long (2026-10-20, Seán): the affiliation,
             the country, or both -->
        <span v-for="(part, index) in affiliationParts" :key="index" class="part">
          {{ part }}{{ index < affiliationParts.length - 1 ? "," : "" }}
        </span>
        <template v-if="!affiliationParts.length">Add affiliation…</template>
      </button>
      <span v-else class="affiliation">{{ affiliationDisplay }}</span>
    </div>
    <p v-if="error" class="field-error" role="alert">{{ error }}</p>
    <!-- edit layers: dark bands docked under the photo (EditLayer), over the rows above -->
    <AppModalProfileEditLayer :is-open="openFields.url" :start-insets="urlStartInsets">
      <button
        class="layer-mark isHomepage"
        type="button"
        title="Close without saving"
        @click="discardField('url')">
        <AppModalProfileHomepageMark is-flush />
      </button>
      <label class="layer-field">
        <span class="layer-prefix">{{ HOMEPAGE_PREFIX }}</span>
        <input
          ref="urlInputRef"
          v-model="drafts.url"
          class="layer-input"
          type="text"
          inputmode="url"
          autocomplete="url"
          spellcheck="false"
          placeholder="yourwebsite.com"
          aria-label="Your homepage, after https://"
          @keydown.enter.prevent="commitAll"
          @keydown.tab.exact.prevent="confirmAndOpenNext('url')"
        />
      </label>
      <template #actions>
        <AppModalProfileEditButtons
          :is-busy="isCommitting"
          :can-cancel="isChanged.url"
          @cancel="discardField('url')"
          @ok="commitAll" />
      </template>
    </AppModalProfileEditLayer>
    <AppModalProfileEditLayer :is-open="openFields.linkedIn" :start-insets="linkedInStartInsets">
      <button
        class="layer-mark"
        type="button"
        title="Close without saving"
        @click="discardField('linkedIn')">
        <AppModalProfileLinkedInMark is-brand />
      </button>
      <label class="layer-field">
        <span class="layer-prefix">{{ LINKEDIN_PREFIX }}</span>
        <input
          ref="linkedInInputRef"
          :value="drafts.linkedInSlug"
          class="layer-input"
          :class="{isInvalid: isLinkedInInvalid}"
          type="text"
          autocomplete="off"
          spellcheck="false"
          placeholder="your-name"
          aria-label="Your LinkedIn name, after linkedin.com/in/"
          :aria-invalid="isLinkedInInvalid"
          @input="handleLinkedInInput"
          @keydown.enter.prevent="commitAll"
          @keydown.tab.exact.prevent="confirmAndOpenNext('linkedIn')"
        />
      </label>
      <template #actions>
        <AppModalProfileEditButtons
          :is-busy="isCommitting"
          :can-cancel="isChanged.linkedIn"
          @cancel="discardField('linkedIn')"
          @ok="commitAll" />
      </template>
    </AppModalProfileEditLayer>
    <AppModalProfileEditLayer :is-open="openFields.affiliation" direction="down">
      <AppModalProfileFieldPair
        ref="affiliationInputRef"
        v-model:first="drafts.affiliation"
        v-model:second="drafts.country"
        :labels="['Affiliation', 'Country']"
        :autocomplete="['organization', 'country-name']"
        :max-length="AFFILIATION_MAX_LENGTH"
        @commit="commitAll"
        @tab="confirmAndOpenNext('affiliation')"
      />
      <template #actions>
        <AppModalProfileEditButtons
          :is-busy="isCommitting"
          :can-cancel="isChanged.affiliation"
          @cancel="discardField('affiliation')"
          @ok="commitAll" />
      </template>
    </AppModalProfileEditLayer>
  </div>
</template>

<script setup>
import {computed, nextTick, ref} from "vue";
import AppModalProfileEditLayer from "@/components/App/Modal/Profile/EditLayer";
import AppModalProfileEditButtons from "@/components/App/Modal/Profile/EditButtons";
import AppModalProfileFieldPair from "@/components/App/Modal/Profile/FieldPair";
import AppModalProfileHomepageMark from "@/components/App/Modal/Profile/HomepageMark";
import AppModalProfileLinkedInMark from "@/components/App/Modal/Profile/LinkedInMark";
import useUserSafe from "@/composables/useUserSafe";
import {
  AFFILIATION_MAX_LENGTH, HOMEPAGE_PREFIX, LINKEDIN_PREFIX,
  getHomepageRest, getLinkedInInput, getLinkedInSlug, getIsLinkedInSlugInvalid,
  useDiscardOnOutside, useProfileFieldEdits
} from "@/composables/useProfileFieldEdits";

// isSettings: the Settings tab's header (centred; the name comes from the slot; the homepage
// icon before it, LinkedIn after it)
const props = defineProps({
  editable: {type: Boolean, default: false},
  isSettings: {type: Boolean, default: false}
});

const {user} = useUserSafe();
const {
  openFields, drafts, error, isAnyOpen, isCommitting, commit, commitField, discardField, discardAll
} = useProfileFieldEdits();
const LAYER_PADDING = 12; // EditLayer's side padding: its icon starts where the clicked one is
const urlIconRef = ref(null);
const linkedInIconRef = ref(null);
const urlStartInsets = ref({});
const linkedInStartInsets = ref({});
const urlInputRef = ref(null);
const linkedInInputRef = ref(null);
const affiliationInputRef = ref(null);

const name = computed(() => {
  const {firstName, lastName} = user.value || {};
  return `${firstName || ""} ${lastName || ""}`.trim();
});

const personalUrl = computed(() => user.value?.publicMetadata?.url);
const linkedInUrl = computed(() => user.value?.publicMetadata?.linkedInUrl);
const hasLinkedIn = computed(() => !!linkedInUrl.value);
const affiliation = computed(() => user.value?.publicMetadata?.affiliation);
const country = computed(() => user.value?.publicMetadata?.country);
const affiliationParts = computed(() => [affiliation.value, country.value].filter(Boolean));
const affiliationDisplay = computed(() => {
  return affiliationParts.value.join(", ");
});

// per field: a draft differing from the saved value (else the layer's Cancel is disabled)
const isChanged = computed(() => ({
  url: getHomepageRest(drafts.url) !== getHomepageRest(personalUrl.value),
  linkedIn: drafts.linkedInSlug !== getLinkedInSlug(linkedInUrl.value),
  affiliation: drafts.affiliation !== (affiliation.value || "")
    || drafts.country !== (country.value || ""),
}));

const isLinkedInInvalid = computed(() => getIsLinkedInSlugInvalid(drafts.linkedInSlug));

// a pasted LinkedIn address becomes just its name, straight away (getLinkedInInput)
function handleLinkedInInput(event) {
  const value = getLinkedInInput(event.target.value);
  drafts.linkedInSlug = value;
  if (event.target.value !== value) event.target.value = value;
}

const urlIconTitle = computed(() => {
  if (!props.editable) return personalUrl.value ? "Visit website" : "";
  return personalUrl.value ? "Edit your homepage URL" : "Add your homepage URL";
});
const linkedInTitle = computed(() => {
  if (!props.editable) return hasLinkedIn.value ? "View LinkedIn profile" : "";
  return hasLinkedIn.value ? "Edit your LinkedIn URL" : "Add your LinkedIn URL";
});

// where an "across" layer starts: a box around the clicked icon, inside the layer's parent
function getStartInsets(iconElement) {
  const parent = iconElement?.offsetParent;
  if (!parent) return {};
  const [icon, box] = [iconElement.getBoundingClientRect(), parent.getBoundingClientRect()];
  return {
    "--start-left": `${icon.left - box.left - LAYER_PADDING}px`,
    "--start-right": `${box.right - icon.right - LAYER_PADDING}px`
  };
}

// opening a field fills its draft and focuses it; saving is the layer's OK (EditButtons)
async function openField(fieldName, inputRef) {
  openFields[fieldName] = true;
  await nextTick();
  // no scroll: mid-transition the input is still outside the narrow layer, and scrolling it into
  // view shifted the whole dialog sideways
  inputRef.value?.focus({preventScroll: true});
}

// each opener fills the field only when it isn't already open (keeps typed text), preferring a
// draft left in local storage over the saved value
function openHomepage() {
  const savedRest = getHomepageRest(personalUrl.value);
  if (!openFields.url) drafts.url = getHomepageRest(drafts.url) || savedRest;
  urlStartInsets.value = getStartInsets(urlIconRef.value);
  openField("url", urlInputRef);
}

function openLinkedIn() {
  const savedSlug = getLinkedInSlug(linkedInUrl.value);
  linkedInStartInsets.value = getStartInsets(linkedInIconRef.value);
  if (!openFields.linkedIn) drafts.linkedInSlug = drafts.linkedInSlug || savedSlug;
  openField("linkedIn", linkedInInputRef);
}

function openAffiliation() {
  if (!openFields.affiliation) drafts.affiliation = drafts.affiliation || affiliation.value || "";
  if (!openFields.affiliation) drafts.country = drafts.country || country.value || "";
  openField("affiliation", affiliationInputRef);
}

// Keyboard: Enter = OK; Tab confirms this field, then opens the next one
// (homepage → LinkedIn → affiliation/country → done); Escape discards, not the dialog
const OPEN_NEXT = {url: openLinkedIn, linkedIn: openAffiliation, affiliation: null};

function commitAll() {
  commit(user.value);
}

async function confirmAndOpenNext(fieldName) {
  const isConfirmed = await commitField(fieldName, user.value);
  if (isConfirmed) OPEN_NEXT[fieldName]?.();
}

// Escape or a click outside the open field discards it (useDiscardOnOutside)
useDiscardOnOutside(() => props.editable && isAnyOpen.value, discardAll);
</script>

<style scoped>
.profile-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.name-row {
  display: flex;
  align-items: center;
  gap: 6px; /* the name, then the homepage and LinkedIn icons */
}
.name {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
}
.meta-icons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.icon-btn {
  appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  transition: color 0.15s;
}
.icon-btn:focus {
  outline: none;
}
.icon-btn:hover:not(.dimmed) {
  color: rgba(0, 0, 0, 0.85);
}
.icon-btn.dimmed {
  color: rgba(0, 0, 0, 0.25);
}
.icon-btn.dimmed:hover {
  color: rgba(0, 0, 0, 0.5);
}
.icon-btn svg {
  width: 16px;
  height: 16px;
}
/* while a field is open, only that field can be selected */
.profile-meta.isEditing .name-row,
.profile-meta.isEditing .affiliation-row {
  user-select: none;
}
.icon-btn.isHidden {
  visibility: hidden;
}
/* content of the edit layers (EditLayer): icon, white prefix text, white inputs */
.layer-mark {
  appearance: none;
  -webkit-appearance: none;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: none;
  box-shadow: none;
  color: #0a66c2; /* LinkedIn blue (its mark has white letters: is-brand) */
  cursor: pointer;
}
.layer-mark:focus {
  outline: none;
}
.layer-mark svg {
  width: 16px;
  height: 16px;
}
.layer-mark.isHomepage {
  color: white;
}
.layer-field {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center; /* icon, prefix and input on one centred line */
  gap: 3px;
  font-size: 15px;
  line-height: 20px;
  cursor: text;
}
.layer-prefix {
  flex: none;
  color: white;
  white-space: nowrap;
  user-select: none;
}
/* the only white surfaces: plain rectangles inside the dark layer */
.layer-input {
  flex: 1;
  min-width: 0;
  height: 24px;
  margin: 0;
  padding: 0 8px;
  box-sizing: border-box;
  border: none;
  border-radius: 0;
  outline: none;
  background: white;
  font: inherit;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.75);
  user-select: text; /* the rows around it are unselectable while editing */
}
.layer-input:focus {
  box-shadow: inset 0 0 0 2px var(--brand-color);
}
.layer-input.isInvalid {
  color: #dc2204; /* red: won't be saved */
}
.layer-input::placeholder {
  font-weight: 300;
  font-style: italic;
  color: rgba(0, 0, 0, 0.4);
}
.field-error {
  margin: 2px 0 0;
  font-size: 12px;
  color: #dc2204;
}
.affiliation-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.affiliation {
  min-width: 0;
  overflow-wrap: anywhere; /* a long word wraps rather than overflowing the dialog */
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.3;
}
.affiliation.placeholder {
  font-weight: 400;
  font-style: italic;
  color: rgba(0, 0, 0, 0.3);
}
/* global.css styles every button: reset, so it reads as text */
.affiliation.isButton {
  appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  min-width: unset;
  font-family: inherit;
  text-align: center;
  cursor: pointer;
}
.affiliation.isButton:hover {
  color: rgba(0, 0, 0, 0.85);
}
.profile-meta.isSettings {
  align-items: center;
  gap: 4px;
  align-self: stretch; /* the dialog's width, so its lines are cut with "…", not overflowing */
  min-width: 0;
}
.isSettings .name-row {
  justify-content: center;
  max-width: 100%; /* the name shrinks (with "…": Name.vue), not the icons */
}
.isSettings .name-row .icon-btn,
.isSettings .meta-icons {
  flex: none;
}
/* Settings: the affiliation and country on one line, each cut with "…" as needed (Seán,
   2026-10-20); each shrinks by its length, so the longer gives way first */
.isSettings .affiliation-row {
  max-width: 100%;
  min-width: 0;
}
.isSettings .affiliation.isButton {
  display: inline-flex;
  gap: 0.25em; /* a cut affiliation ends in "…", its comma with it (a separate comma sat after
                  the "…" with a gap: the cut leaves up to a letter's width) */
  max-width: 100%;
  min-width: 0;
}
.isSettings .affiliation .part {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
/* the affiliation and email: one size and weight (Email.vue); the name larger (Name.vue) */
.isSettings .affiliation {
  font-size: 14px; /* a clear step below the name (20px): Seán, 2026-10-20; was 16px */
  font-weight: 600;
  color: inherit; /* the name's colour */
}
.isSettings .affiliation.isButton:hover {
  color: rgba(0, 0, 0, 0.65);
}
.isSettings .affiliation.placeholder {
  color: rgba(0, 0, 0, 0.3);
}

.layer-mark:hover {
  opacity: 0.8; /* closes without saving (DESIGN.md § 15) */
}
</style>
